import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';

@Component({
  selector: 'app-privacy-policy',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <div class="min-h-screen bg-polished-charcoal text-silk-white py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-4xl mx-auto space-y-8">
        
        <!-- Barra de Navegación Legal Superior (Tabs UX) -->
        <div class="bg-black/40 backdrop-blur-md p-2 rounded-2xl border border-white/10 flex flex-wrap gap-2 justify-center sm:justify-between items-center text-xs sm:text-sm">
          <a routerLink="/" class="text-amethyst-glint hover:text-white px-3 py-2 rounded-xl transition flex items-center gap-1.5 font-medium">
            ← Inicio
          </a>
          <div class="flex flex-wrap gap-1 sm:gap-2">
            <a routerLink="/terminos" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              ⚖️ Términos
            </a>
            <a routerLink="/privacidad" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🔒 Privacidad
            </a>
            <a routerLink="/garantias-retracto" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🛡️ Garantías
            </a>
            <a routerLink="/aviso-legal" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🏛️ Marcas
            </a>
          </div>
          <button (click)="printPage()" class="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-silk-white/80 hover:text-white transition text-xs border border-white/10">
            🖨️ Imprimir
          </button>
        </div>

        <!-- Tarjeta Principal de Contenido -->
        <div class="bg-black/30 backdrop-blur-md rounded-3xl shadow-2xl p-8 sm:p-12 border border-white/10">
          
          <div class="flex items-center justify-between border-b border-white/10 pb-6 mb-8">
            <div>
              <span class="text-xs text-amethyst-glint uppercase tracking-widest font-bold">Protección de Datos • Colombia</span>
              <h1 class="text-3xl sm:text-4xl font-extrabold text-white mt-1 tracking-tight">
                Política de Privacidad y Tratamiento de Datos (Habeas Data)
              </h1>
            </div>
            <div class="text-right hidden sm:block">
              <p class="text-xs text-silk-white/50">Actualización</p>
              <p class="text-sm font-semibold text-amethyst-glint">Noviembre 2026</p>
            </div>
          </div>

          <p class="text-sm text-silk-white/60 mb-8 bg-white/5 p-4 rounded-xl border border-white/5">
            🔒 Cumplimiento estricto de la <strong>Ley 1581 de 2012</strong> y el <strong>Decreto 1377 de 2013</strong> sobre Habeas Data y protección de información personal.
          </p>

          <div class="space-y-8 text-silk-white/90 leading-relaxed text-sm sm:text-base">
            
            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>1.</span> Responsable del Tratamiento de Datos
              </h2>
              <p class="text-silk-white/80">
                <strong>Altair Parfum</strong> (<a href="https://altairparfum.com" class="text-cyan-400 underline">altairparfum.com</a>), con domicilio en Bogotá D.C., Colombia, es responsable de la recolección, almacenamiento y uso de los datos personales proporcionados por los usuarios a través de la plataforma.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>2.</span> Finalidades del Tratamiento de Datos
              </h2>
              <p class="text-silk-white/80">Los datos suministrados (nombre, cédula, dirección, teléfono y correo) son tratados para:</p>
              <ul class="list-disc pl-6 space-y-2 text-silk-white/80">
                <li>Gestionar, despachar y entregar pedidos de perfumería (botellas y decants), incluyendo la coordinación de pagos contra entrega.</li>
                <li>Generar comprobantes de entrega y reportes logísticos con operadores de transporte.</li>
                <li>Enviar notificaciones sobre el estado de órdenes y confirmaciones de pago.</li>
                <li>Atender peticiones, quejas, reclamos (PQR) y garantías.</li>
                <li>Envío de información comercial y lanzamientos (exclusivamente con autorización previa y expresa del titular).</li>
              </ul>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>3.</span> Derechos de los Titulares (Derechos ARCO)
              </h2>
              <p class="text-silk-white/80">Conforme al artículo 8 de la Ley 1581 de 2012, usted tiene derecho a:</p>
              <ul class="list-disc pl-6 space-y-2 text-silk-white/80">
                <li><strong>Acceso:</strong> Conocer los datos personales que poseemos.</li>
                <li><strong>Actualización y Rectificación:</strong> Corregir datos inexactos o desactualizados.</li>
                <li><strong>Supresión:</strong> Solicitar la eliminación de sus datos cuando finalice la finalidad legal.</li>
                <li><strong>Revocatoria:</strong> Revocar la autorización otorgada para el tratamiento.</li>
              </ul>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>4.</span> Canal Oficial para Habeas Data
              </h2>
              <div class="p-5 bg-black/40 rounded-xl border border-white/10 space-y-2 text-sm">
                <p>📧 <strong>Correo Habeas Data:</strong> contacto&#64;altairparfum.com</p>
                <p>💬 <strong>WhatsApp:</strong> +57 320 340 5747</p>
                <p>📍 <strong>Asunto requerido:</strong> "Habeas Data - Solicitud de Datos"</p>
              </div>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>5.</span> Seguridad de la Información
              </h2>
              <p class="text-silk-white/80">
                Implementamos estrictas medidas técnicas, administrativas y humanas para proteger sus datos personales contra acceso no autorizado, pérdida o divulgación indebida.
              </p>
            </section>

          </div>

          <div class="mt-12 pt-6 border-t border-white/15 flex justify-between items-center">
            <a routerLink="/terminos" class="text-amethyst-glint hover:text-white text-sm transition">
              ← Anterior: Términos y Condiciones
            </a>
            <a routerLink="/garantias-retracto" class="text-amethyst-glint hover:text-white text-sm transition">
              Siguiente: Garantías y Retracto →
            </a>
          </div>

        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivacyPolicyComponent {
  printPage() {
    window.print();
  }
}
